import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchCSRFToken, getCSRFToken } from '../../lib/csrf';
import i18n from '../../i18n';
import css from './survey.module.css';
import { trackSurveyCompleted } from '../../lib/analytics';


const getAvailableLanguages = (t) => [
    { code: "en", name: t("English"), flag: "🇬🇧" },
    { code: "uk", name: t("Ukrainian"), flag: "🇺🇦" },
    { code: "pl", name: t("Polish"), flag: "🇵🇱" },
];

const Survey = ({ authUser }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const particlesContainerRef = useRef(null);

    // State management
    const [currentPage, setCurrentPage] = useState(() => {
        // Check if we should skip language selection
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('skipLanguage') === 'true' ? 'survey' : 'language';
    }); // 'language', 'survey'
    const [currentLanguage, setCurrentLanguage] = useState(
        localStorage.getItem("selectedLanguage") || "en"
    );
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);

    // Survey data
    const [surveyData, setSurveyData] = useState({
        language: null,
        englishLevel: null,
        purpose: null
    });

    // Questions data - store for all languages
    const [questionsData, setQuestionsData] = useState({
        levels: {
            en: [],
            uk: [],
            pl: []
        },
        purposes: {
            en: [],
            uk: [],
            pl: []
        }
    });

    // Current displayed questions based on interface language
    const [levelQuestions, setLevelQuestions] = useState([]);
    const [purposes, setPurposes] = useState([]);

    // Progress tracking
    const [completedSteps, setCompletedSteps] = useState({
        level: false,
        purpose: false
    });

    // Add/remove scrollbar styles
    useEffect(() => {
        document.body.classList.add('surveyPageActive');

        return () => {
            document.body.classList.remove('surveyPageActive');
        };
    }, []);

    // Initialize particles effect
    useEffect(() => {
        if (particlesContainerRef.current) {
            const container = particlesContainerRef.current;
            container.innerHTML = ''; // Clear existing particles

            for (let i = 0; i < 50; i++) {
                const particle = document.createElement('div');
                particle.className = css.particle;
                particle.style.left = Math.random() * 100 + '%';
                particle.style.animationDelay = Math.random() * 15 + 's';
                particle.style.animationDuration = (15 + Math.random() * 10) + 's';
                container.appendChild(particle);
            }
        }
    }, []);

    // Check authentication and load user data
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const res = await axios.get("/api/v1/user-profile/current", {
                    withCredentials: true,
                });
                const lang = res.data.language || "en";
                setCurrentLanguage(lang);
                i18n.changeLanguage(lang);
                setIsAuthenticated(true);
            } catch (error) {
                console.warn("❌ Неавторизований або 403 — працюємо як гість");
                setIsAuthenticated(false);
            }
        };

        fetchUserProfile();
    }, []);

    // Listen for language changes from external sources (like header language switcher)
    useEffect(() => {
        const handleLanguageChange = () => {
            const newLang = i18n.language;
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                
                const levelDescriptions = {
                    en: [
                        "Starting from zero",
                        "Understand simple phrases",
                        "Talk about familiar topics",
                        "Speak fluently",
                        "Discuss complex ideas"
                    ],
                    uk: [
                        "Починаю з нуля",
                        "Розумію прості фрази",
                        "Розмовляю на знайомі теми",
                        "Вільно спілкуюся",
                        "Обговорюю складні ідеї"
                    ],
                    pl: [
                        "Zaczynam od zera",
                        "Rozumiem proste zwroty",
                        "Rozmawiam na znane tematy",
                        "Swobodnie się komunikuję",
                        "Omawiam złożone idee"
                    ]
                };

                const updatedLevels = questionsData.levels[newLang]?.map((level, index) => ({
                    ...level,
                    option: levelDescriptions[newLang][index] || level.option
                }));

                if (updatedLevels) {
                    setLevelQuestions(updatedLevels);
                }

                if (questionsData.purposes[newLang]) {
                    const purposeTranslations = {
                        en: ["Travel and Tourism", "Work and Business", "Education and Learning", "Communication", "Entertainment"],
                        uk: ["Подорожі та туризм", "Робота та бізнес", "Освіта та навчання", "Спілкування", "Розваги"],
                        pl: ["Podróże i turystyka", "Praca i biznes", "Edukacja i nauka", "Komunikacja", "Rozrywka"]
                    };

                    const updatedPurposes = questionsData.purposes[newLang].map((purpose, index) => ({
                        ...purpose,
                        purpose: purposeTranslations[newLang][index] || purpose.purpose
                    }));
                    setPurposes(updatedPurposes);
                }
            }
        };

        i18n.on('languageChanged', handleLanguageChange);
        handleLanguageChange(); // Call immediately to set initial translations

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [currentLanguage, questionsData, i18n]);

    // Update displayed questions when interface language changes
    useEffect(() => {
        const currentLang = i18n.language;
        
        const translations = {
            en: {
                levels: [
                    "Ideal for beginners",
                    "Starting from zero",
                    "Understand simple phrases",
                    "Talk about familiar topics",
                    "Speak fluently",
                    "Discuss complex ideas"
                ],
                purposes: [
                    "Travel preparation",
                    "Job interview",
                    "Educational goals",
                    "Communication with people",
                    "For fun"
                ]
            },
            uk: {
                levels: [
                    "Ідеально для початківців",
                    "Починаю з нуля",
                    "Розумію прості фрази",
                    "Розмовляю на знайомі теми",
                    "Вільно спілкуюся",
                    "Обговорюю складні ідеї"
                ],
                purposes: [
                    "Підготовка до подорожі",
                    "Для співбесіди на роботу",
                    "Освітні цілі",
                    "Спілкування з людьми",
                    "Задля розваги"
                ]
            },
            pl: {
                levels: [
                    "Idealny dla początkujących",
                    "Zaczynam od zera",
                    "Rozumiem proste zwroty",
                    "Rozmawiam na znane tematy",
                    "Swobodnie się komunikuję",
                    "Omawiam złożone idee"
                ],
                purposes: [
                    "Przygotowanie do podróży",
                    "Rozmowa kwalifikacyjna",
                    "Cele edukacyjne",
                    "Komunikacja z ludźmi",
                    "Dla zabawy"
                ]
            }
        };

        const currentTranslations = translations[currentLang] || translations.en;

        // Update level questions
        const newLevelQuestions = currentTranslations.levels.map((text, index) => ({
            id: index === 0 ? 7 : index, // pre-A1 has ID 7
            option: text,
            levels: index === 0 ? ['pre-A1'] : (index === 5 ? ['C1', 'C2'] : [['A1', 'A2', 'B1', 'B2'][index - 1]])
        }));
        setLevelQuestions(newLevelQuestions);

        // Update purposes
        const newPurposes = currentTranslations.purposes.map((text, index) => ({
            id: index + 1,
            purpose: text,
            categories: [index + 1]
        }));
        setPurposes(newPurposes);

        // If we don't have data for the current language and we're on the survey page, fetch it
        if (currentPage === 'survey' &&
            (!questionsData.levels[currentLang]?.length || !questionsData.purposes[currentLang]?.length)) {

            const fetchMissingLanguageData = async () => {
                try {
                    const [levelsRes, purposesRes] = await Promise.all([
                        axios.get("/api/v1/language-level-questionnaire", {
                            withCredentials: true,
                            headers: {
                                'Accept-Language': currentLang
                            }
                        }),
                        axios.get("/api/v1/language-purpose-questionnaire", {
                            withCredentials: true,
                            headers: {
                                'Accept-Language': currentLang
                            }
                        })
                    ]);

                    setQuestionsData(prev => ({
                        ...prev,
                        levels: {
                            ...prev.levels,
                            [currentLang]: levelsRes.data.results || []
                        },
                        purposes: {
                            ...prev.purposes,
                            [currentLang]: purposesRes.data.results || []
                        }
                    }));

                    setLevelQuestions(levelsRes.data.results || []);
                    setPurposes(purposesRes.data.results || []);
                } catch (error) {
                    console.error(`Error fetching missing data for ${currentLang}:`, error);
                }
            };

            fetchMissingLanguageData();
        }
    }, [i18n.language, questionsData, currentPage]);

    // Load survey questions for all languages
    useEffect(() => {
        const fetchQuestionsForAllLanguages = async () => {
            try {
                const languages = ['en', 'uk', 'pl'];
                const allQuestionsData = {
                    levels: { en: [], uk: [], pl: [] },
                    purposes: { en: [], uk: [], pl: [] }
                };

                // Fetch questions for each language
                for (const lang of languages) {
                    try {
                        const [levelsRes, purposesRes] = await Promise.all([
                            axios.get("/api/v1/language-level-questionnaire", {
                                withCredentials: true,
                                headers: {
                                    'Accept-Language': lang
                                }
                            }),
                            axios.get("/api/v1/language-purpose-questionnaire", {
                                withCredentials: true,
                                headers: {
                                    'Accept-Language': lang
                                }
                            })
                        ]);

                        allQuestionsData.levels[lang] = levelsRes.data.results || [];
                        allQuestionsData.purposes[lang] = purposesRes.data.results || [];
                    } catch (error) {
                        console.error(`Error fetching questions for ${lang}:`, error);
                        // Continue with other languages even if one fails
                    }
                }

                setQuestionsData(allQuestionsData);

                // Set initial display based on current language
                const currentLang = i18n.language;
                setLevelQuestions(allQuestionsData.levels[currentLang] || []);
                setPurposes(allQuestionsData.purposes[currentLang] || []);

            } catch (error) {
                console.error("Error fetching questions:", error);
            }
        };

        if (currentPage === 'survey') {
            fetchQuestionsForAllLanguages();
        }
    }, [currentPage]); // Remove i18n.language dependency since we fetch all languages

    // Handle language selection
    const handleLanguageSelect = async (langCode) => {
        setLoading(true);
        try {
            document.cookie = `django_language=${langCode}; path=/`;
            i18n.changeLanguage(langCode);
            localStorage.setItem("selectedLanguage", langCode);
            setCurrentLanguage(langCode);

            if (isAuthenticated) {
                let csrfToken = getCSRFToken();
                if (!csrfToken) {
                    await fetchCSRFToken();
                    csrfToken = getCSRFToken();
                }

                await axios.patch(
                    "/api/v1/user-profile/current",
                    { language: langCode },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        withCredentials: true,
                    }
                );
            }

            setSurveyData(prev => ({ ...prev, language: langCode }));
            setCurrentPage('survey');
        } catch (error) {
            console.error("Помилка при виборі мови:", error.response?.data || error);
        } finally {
            setLoading(false);
        }
    };

    // Handle level selection
    const handleLevelSelect = (questionData, e) => {
        const selectedLevel = questionData.levels[questionData.levels.length - 1];
        
        // Store both the questionnaire ID and the level code
        setSurveyData(prev => ({ 
            ...prev, 
            englishLevel: selectedLevel,
            levelQuestionnaireId: questionData.id // Store the questionnaire ID
        }));

        // Update completed steps and handle scroll logic
        setCompletedSteps(prev => {
            const newSteps = { ...prev, level: true };

            // Smart scroll logic - use setTimeout to ensure state is updated
            setTimeout(() => {
                if (!newSteps.purpose) {
                    // If purpose not answered, scroll to purpose question
                    const purposeCard = document.querySelector('[data-question="2"]');
                    if (purposeCard) {
                        purposeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                } else {
                    // If purpose already answered, scroll to submit section
                    const submitSection = document.getElementById('submitSection');
                    if (submitSection) {
                        submitSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);

            return newSteps;
        });

        // Store the questionnaire ID for API calls
        localStorage.setItem("selectedLevel", questionData.id.toString());
        localStorage.setItem("selectedLevelCode", selectedLevel);
        console.log('Selected level questionnaire ID:', questionData.id, 'Level code:', selectedLevel);
    };

    // Handle purpose selection
    const handlePurposeSelect = (purposeObj) => {
        const purposeData = {
            id: purposeObj.id,
            purpose: purposeObj.purpose,
            categories: purposeObj.categories || [],
        };

        setSurveyData(prev => ({ ...prev, purpose: purposeData }));

        // Update completed steps and handle scroll logic
        setCompletedSteps(prev => {
            const newSteps = { ...prev, purpose: true };

            // Smart scroll logic - use setTimeout to ensure state is updated
            setTimeout(() => {
                if (!newSteps.level) {
                    // If level not answered, scroll to level question
                    const levelCard = document.querySelector('[data-question="1"]');
                    if (levelCard) {
                        levelCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                } else {
                    // If level already answered, scroll to submit section
                    const submitSection = document.getElementById('submitSection');
                    if (submitSection) {
                        submitSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);

            return newSteps;
        });

        localStorage.setItem("selectedPurpose", JSON.stringify(purposeData));
    };

    // Handle step dot click
    const handleStepClick = (stepNumber) => {
        const targetCard = document.querySelector(`[data-question="${stepNumber}"]`);
        if (targetCard) {
            targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Submit survey
    const handleSubmitSurvey = async () => {
        setLoading(true);

        try {
            console.log('Survey Data:', surveyData);
            console.log('Level Questionnaire ID:', surveyData.levelQuestionnaireId);
            console.log('Purpose ID:', surveyData.purpose?.id);

            // Store the IDs for fetching learning paths
            if (surveyData.levelQuestionnaireId) {
                localStorage.setItem("selectedLevel", surveyData.levelQuestionnaireId.toString());
            }
            if (surveyData.purpose) {
                localStorage.setItem("selectedPurpose", JSON.stringify(surveyData.purpose));
            }

            // Get level_cefr from selectedLevelCode or surveyData
            const levelCefr = localStorage.getItem("selectedLevelCode") || surveyData.englishLevel || '';
            
            // Map purpose to goal format (work/travel/study/social/kids)
            let goal = '';
            if (surveyData.purpose?.purpose) {
                const purposeText = surveyData.purpose.purpose.toLowerCase();
                if (purposeText.includes('travel') || purposeText.includes('подорож') || purposeText.includes('podróże')) {
                    goal = 'travel';
                } else if (purposeText.includes('work') || purposeText.includes('job') || purposeText.includes('робот') || purposeText.includes('praca')) {
                    goal = 'work';
                } else if (purposeText.includes('education') || purposeText.includes('study') || purposeText.includes('освіт') || purposeText.includes('edukacja')) {
                    goal = 'study';
                } else if (purposeText.includes('communication') || purposeText.includes('спілкування') || purposeText.includes('komunikacja')) {
                    goal = 'social';
                } else if (purposeText.includes('entertainment') || purposeText.includes('розваг') || purposeText.includes('zabaw')) {
                    goal = 'kids';
                }
            }
            
            // Schedule is not implemented yet, using empty string as placeholder
            const schedule = '';

            // Track survey completion
            trackSurveyCompleted(levelCefr, goal, schedule);

            // Success animation
            confetti();

            // Redirect after delay
            setTimeout(() => {
                navigate("/roadMap");
            }, 1500);

        } catch (error) {
            console.error("Error submitting survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // Confetti animation
    const confetti = () => {
        const colors = ['#9333ea', '#6366f1', '#a855f7', '#818cf8'];
        const confettiCount = 100;

        for (let i = 0; i < confettiCount; i++) {
            const confettiEl = document.createElement('div');
            confettiEl.style.position = 'fixed';
            confettiEl.style.width = '10px';
            confettiEl.style.height = '10px';
            confettiEl.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            confettiEl.style.left = Math.random() * 100 + '%';
            confettiEl.style.top = '-10px';
            confettiEl.style.opacity = Math.random() + 0.5;
            confettiEl.style.transform = `rotate(${Math.random() * 360}deg)`;
            confettiEl.style.transition = 'all 3s ease-out';
            confettiEl.style.zIndex = '9999';
            document.body.appendChild(confettiEl);

            setTimeout(() => {
                confettiEl.style.top = '100%';
                confettiEl.style.transform = `rotate(${Math.random() * 360}deg) translateX(${(Math.random() - 0.5) * 200}px)`;
                confettiEl.style.opacity = '0';
            }, 10);

            setTimeout(() => confettiEl.remove(), 3000);
        }
    };

    return (
        <div className={css.surveyContainer}>
            {/* Background Effects */}
            <div className={css.background}></div>
            {/*
            <div className={css.orb1}></div>
            <div className={css.orb2}></div>
            <div className={css.orb3}></div>
            <div className={css.particles} ref={particlesContainerRef}></div>
*/}
            {/* Language Selection Page */}
            {currentPage === 'language' && (
                <div className={`${css.page} ${css.languagePage} ${css.active}`}>
                    <div className={css.languageContainer}>
                        <h1 className={css.languageTitle}>
                            {t("In which language would you like to view the website?")}
                        </h1>
                        <div className={css.languageGrid}>
                            {getAvailableLanguages(t).map((lang) => (
                                <div
                                    key={lang.code}
                                    className={css.languageCard}
                                    onClick={() => handleLanguageSelect(lang.code)}
                                >
                                    <span className={css.languageFlag}>{lang.flag}</span>
                                    <div className={css.languageName}>{lang.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Survey Page */}
            {currentPage === 'survey' && (
                <div className={`${css.page} ${css.surveyPage} ${css.active}`}>
                    {/* Progress Steps */}
                    <div className={css.progressSteps}>
                        <div
                            className={`${css.stepDot} ${completedSteps.level ? css.completed : css.active}`}
                            data-step="1"
                            onClick={() => handleStepClick(1)}
                        ></div>
                        <div
                            className={`${css.stepDot} ${completedSteps.purpose ? css.completed : (completedSteps.level ? css.active : '')}`}
                            data-step="2"
                            onClick={() => handleStepClick(2)}
                        ></div>
                    </div>

                    {/* Hero Section */}
                    <div className={css.hero}>
                        <h1 className={css.heroTitle}>
                            {t("Take the test — get a program personalized to your goal and level")}
                        </h1>
                        <p className={css.heroSubtitle}>
                            {t("It will take less than 1 minute")}
                        </p>
                        {/*  <div className={css.heroBadge}>
                    <span>🚀</span>
                    <span>{t("Quick & Easy")}</span>
                </div>*/}
                    </div>

                    <div className={css.container}>
                        {/* Question 1: English Level */}
                        <div className={`${css.questionCard} ${css.visible}`} data-question="1">
                            {/*<div className={css.questionHeader}>
                                <span className={css.questionNumber}>
                                    <span>{t("Step 1 of 2")}</span>
                                </span>
                                <div className={css.questionIcon}>📊</div>
                            </div>*/}
                            <h2 className={css.questionTitle}>
                                {t("What is your current English level?")}
                            </h2>
                            <div className={css.options}>
                                {levelQuestions.map((question, index) => {
                                    const currentLevel = question.levels[question.levels.length - 1];
                                    const isSelected = surveyData.englishLevel === currentLevel;
                                    const levelNames = ["pre-A1", 'A1', 'A2', 'B1', 'B2', 'C1-C2'];
                                    const levelName = levelNames[index];
                                   // console.log(question);

                                    return (
                                        <div
                                            key={question.id}
                                            className={`${css.option} ${isSelected ? css.selected : ''} ${index >= 3 ? css.blocked : ""}`}
                                            onClick={() => {
                                                if(index >= 3) {
                                                    return
                                                } else {
                                                handleLevelSelect(question)
                                            }
                                            } }
                                        >
                                            {index >= 3 && (<svg className={css.lockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0110 0v4"></path>
                                            </svg>)}
                                            <span className={css.levelCode}>{levelName}</span>
                                            <p className={css.levelDescription}>{getLocalizedLevelDescription(question.option, t)}</p>
                                            {index >= 4 && (<span className={css.comingSoon}>{t("coming soon")}</span>)}
                                            <div className={css.checkIcon}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M3 8L6 11L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Question 2: Purpose */}
                        <div className={`${css.questionCard} ${css.visible}`} data-question="2">
                            {/*<div className={css.questionHeader}>
                                <span className={css.questionNumber}>
                                    <span>{t("Step 2 of 2")}</span>
                                </span>
                                <div className={css.questionIcon}>🎯</div>
                            </div>*/}
                            <h2 className={css.questionTitle}>
                                {t("What's your main goal with English?")}
                            </h2>
                            <div className={css.options}>
                                {purposes.map((purpose) => {
                                    const isSelected = surveyData.purpose?.id === purpose.id;

                                    return (
                                        <div
                                            key={purpose.id}
                                            className={`${css.optionP} ${isSelected ? css.selected : ''}`}
                                            onClick={() => handlePurposeSelect(purpose)}
                                        >
                                            <div className={css.purposeIcon}>
                                                {getPurposeIcon(purpose.purpose)}
                                            </div>
                                            <div className={css.optionText}>{purpose.purpose}</div>
                                            <div className={css.checkIcon}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M3 8L6 11L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit Section */}
                        {completedSteps.level && completedSteps.purpose && (
                            <div className={`${css.submitSection} ${css.show}`} id="submitSection">
                                <h3 className={css.completionMessage}>
                                    🎉 {t("Great! We've prepared your personalized learning path")}
                                </h3>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                        className={css.getPlanBtn}
                                        onClick={handleSubmitSurvey}
                                        disabled={loading}
                                    >
                                        <div className={css.getPlanBtnContent}>
                                            {!loading && <span className={css.getPlanBtnIcon}>✨</span>}
                                            <span>
                                                {loading ? t("Processing...") : t("Finish test")}
                                            </span>
                                            {loading && <div className={css.loading}></div>}
                                        </div>
                                    </button>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper function to get purpose icons
const getPurposeIcon = (purpose) => {
    const purposeText = purpose.toLowerCase();
    if (purposeText.includes('travel') || purposeText.includes('подорож') || purposeText.includes('podróży') ) return '✈️';
    if (purposeText.includes('job') || purposeText.includes('робот') || purposeText.includes('praca') || purposeText.includes('kwalifikacyjna')) return '💼';
    if (purposeText.includes('education') || purposeText.includes('освіт') || purposeText.includes('edukac')) return '🎓';
    if (purposeText.includes('communication') || purposeText.includes('спілкування') || purposeText.includes('komunikac')) return '💬';
    if (purposeText.includes('entertainment') || purposeText.includes('розваг') || purposeText.includes('rozrywk') || purposeText.includes('fun') || purposeText.includes('zabawy')) return '🎬';
    return '🎯'; // default icon
};

// Helper function to get localized level description
const getLocalizedLevelDescription = (option, t) => {
    // Since we now fetch localized content from API, just return the option text
    // The API already provides the text in the correct language
    return option;
};

export default Survey;